// Fuente única de los trabajos que mostramos.
//
// Antes esta lista estaba duplicada en PortfolioSummary.tsx (3 destacados) y en
// Portfolio.tsx (los 7), con textos que ya habían empezado a divergir entre una y otra.
// Ahora las dos leen de acá, y la ficha de detalle (/portafolio/:slug) también.
//
// El `slug` es parte de la URL pública y de los links que compartimos: una vez que un
// caso se compartió por WhatsApp o LinkedIn, cambiarle el slug rompe ese link. Si hay
// que renombrar uno, agregar la redirección en vez de editarlo.

export interface Captura {
    src: string;
    alt: string;
    caption?: string;
}

export interface Metrica {
    valor: string;
    label: string;
}

export interface Proyecto {
    slug: string;
    titulo: string;
    /** Bajada corta. Es lo que sale en el preview del link al compartirlo. */
    subtitulo: string;
    cliente?: string;
    anio: string;
    /**
     * Etiqueta corta. Es a la vez el badge de la card y el botón del filtro del
     * portafolio, así que el set tiene que quedar chico: una categoría por proyecto
     * convierte el filtro en una lista de botones que no filtran nada. Hoy son cinco:
     * Sitio institucional · E-Commerce · SaaS · IoT · Sistemas de gestión.
     */
    categoria: string;
    /** Descripción de una o dos líneas para las cards. */
    resumen: string;
    /** Imagen de la card. Ruta absoluta desde public/. Puede ser SVG. */
    imagen: string;
    /**
     * Imagen del preview al compartir el link, si `imagen` no sirve para eso. Los
     * crawlers de WhatsApp y LinkedIn no renderizan SVG: una portada vectorial se ve
     * perfecta en el sitio y deja el preview en blanco. Por defecto se usa `imagen`.
     */
    imagenSocial?: string;
    /** Si va en la home (sección "Trabajos Realizados"). */
    destacado: boolean;
    /** Sitio público, si lo hay. Los sistemas internos no tienen. */
    url?: string;
    /** Galería de la ficha. Vacía = la ficha muestra solo la imagen principal. */
    capturas: Captura[];
    /** El caso contado en prosa, un string por párrafo. */
    historia: string[];
    desafio?: string;
    solucion?: string;
    metricas: Metrica[];
    tecnologias: string[];
    rol?: string;
    duracion?: string;
    /**
     * true = es un sistema interno de un cliente y no podemos mostrar capturas con datos
     * reales. La ficha lo dice explícitamente en vez de dejar huecos sin explicación.
     */
    confidencial?: boolean;
}

export const proyectos: Proyecto[] = [
    // ─────────────────────────────────────────────────────────────────────────
    {
        slug: 'fundacion-estrellas-amarillas',
        titulo: 'Fundación Estrellas Amarillas',
        subtitulo: 'El sitio y la plataforma de gestión de una fundación federal de seguridad vial',
        cliente: 'Fundación Estrellas Amarillas',
        anio: '2026',
        categoria: 'Sitio institucional',
        resumen:
            'Sitio público, mapa federal de 3.300+ estrellas georreferenciadas y un backoffice para que ' +
            'la fundación publique sin depender de nosotros.',
        imagen: '/images/casos/fundacion-hero.jpg',
        destacado: true,
        url: 'https://www.fundacionestrellasamarillas.org.ar/',
        capturas: [
            {
                src: '/images/casos/fundacion-hero.jpg',
                alt: 'Home del sitio de la Fundación Estrellas Amarillas',
                caption: 'La home. La estrella pintada sobre el asfalto es la imagen que la fundación usa hace veinte años; el sitio se construyó alrededor de eso.',
            },
            {
                src: '/images/casos/fundacion-mapa.jpg',
                alt: 'Mapa de Argentina con las estrellas amarillas georreferenciadas',
                caption: 'El mapa de estrellas. Cada punto es un lugar donde la fundación acompañó a una familia. Se filtra por provincia, por tipo de alerta y se puede ver como mapa de calor.',
            },
            {
                src: '/images/casos/fundacion-datos.jpg',
                alt: 'Panel de datos y estadísticas abiertas',
                caption: 'Datos abiertos. Además de las estrellas, la plataforma registra baches, falta de señalización y obras sin señalizar: alertas viales que la fundación releva y publica.',
            },
            {
                src: '/images/casos/fundacion-graficos.jpg',
                alt: 'Gráficos de distribución por tipo y provincias con más alertas',
                caption: 'Los gráficos se arman solos con lo que hay cargado. Nadie actualiza un Excel para que esto exista.',
            },
            {
                src: '/images/casos/fundacion-pedi-estrella.jpg',
                alt: 'Formulario "Pedí tu estrella"',
                caption: '"Pedí tu estrella". El formulario más delicado del sitio: lo completa una familia que perdió a alguien.',
            },
        ],
        historia: [
            'Las estrellas amarillas que ves pintadas al costado de una ruta no son decoración. Cada una marca el lugar exacto donde alguien murió en un siniestro vial. Las pinta una fundación que arrancó hace veinte años en La Pampa, con familias que decidieron hacer algo con el dolor en vez de quedarse con él.',
            'Cuando nos buscaron, la fundación tenía veinte años de trabajo y no tenía dónde ponerlo. Las estrellas estaban en planillas, en fotos de WhatsApp, en la cabeza de la gente que las pintó. Las charlas en escuelas se anotaban en un cuaderno. Y cada vez que querían publicar una noticia, tenían que pedirle a alguien que supiera tocar HTML.',
            'La primera conversación no fue sobre tecnología. Fue Silvia contándonos cómo es acompañar a una familia el día que pinta la estrella de su hijo. Nos quedó claro rápido que este no era un sitio para lucirse: era un sitio que tenía que ser sobrio, cargar rápido en la ruta con dos rayitas de señal, y sobre todo no fallarle a alguien que lo abre en el peor momento de su vida.',
            'De ahí salió todo lo demás. El mapa existe porque veinte años de estrellas dispersas, juntas, dicen algo que ninguna estrella sola puede decir: son 3.300 puntos y se ven las rutas donde se muere más gente. El formulario de "Pedí tu estrella" lo reescribimos tres veces hasta sacarle todo lo que sonara a trámite. Y el backoffice existe porque la fundación tenía que poder publicar una noticia un domingo a la noche sin llamarnos.',
            'Eso último es, para nosotros, la parte que más importa. Hoy cargan noticias, capacitaciones, acciones institucionales, recursos descargables y estrellas nuevas sin que intervengamos. Nosotros mantenemos la plataforma; el contenido es de ellos. Un sitio que necesita al desarrollador para actualizarse es un sitio que se muere a los seis meses.',
            'Lo hicimos a precio de fundación, que es una manera elegante de decir que no fue el proyecto más rentable del año. Fue el que volveríamos a tomar sin pensarlo.',
        ],
        desafio:
            'Veinte años de trabajo territorial repartidos entre planillas, fotos y memoria de la gente. Sin un lugar donde mostrarlo y sin autonomía para publicar: cada cambio dependía de que alguien con conocimientos técnicos estuviera disponible.',
        solucion:
            'Un sitio público rápido y sobrio, un mapa federal que georreferencia cada estrella y cada alerta vial, un módulo de datos abiertos con estadísticas que se arman solas, y un backoffice completo para que la fundación gestione todo su contenido sin intermediarios.',
        metricas: [
            { valor: '3.300+', label: 'estrellas georreferenciadas' },
            { valor: '24', label: 'provincias alcanzadas' },
            { valor: '100%', label: 'del contenido lo carga la fundación' },
            { valor: '0', label: 'llamados para publicar una noticia' },
        ],
        tecnologias: ['Angular', 'Java 21', 'Spring Boot 3', 'MySQL', 'Liquibase', 'Redis', 'Google Maps API', 'PWA', 'Mercado Pago'],
        rol: 'Producto, diseño, desarrollo full-stack, infraestructura y mantenimiento',
        duracion: 'En evolución continua',
    },

    // ─────────────────────────────────────────────────────────────────────────
    // NOTA: el cliente de este proyecto exige aislamiento y el código se le transfiere.
    // Por eso la ficha no lo nombra ni nombra a su ERP. Si alguna vez autoriza que lo
    // digamos, es cambiar `cliente` y listo — el texto no depende de eso.
    {
        slug: 'portal-b2b-mayorista',
        titulo: 'Portal B2B mayorista integrado a ERP',
        subtitulo: 'Un canal de venta online para revendedores, con el ERP del cliente como fuente de verdad',
        cliente: 'Distribuidora industrial (bajo acuerdo de confidencialidad)',
        anio: '2026',
        categoria: 'E-Commerce',
        resumen:
            'Catálogo, precios y stock sincronizados con el ERP cada pocos minutos. El revendedor entra ' +
            'con su CUIT, ve su lista de precios y el pedido cae como Nota de Pedido en el ERP.',
        // Diagrama propio en vez de una captura: las pantallas reales tienen precios y
        // clientes del cliente. Ver `confidencial` más abajo.
        imagen: '/images/casos/portal-b2b-portada.svg',
        imagenSocial: '/images/logo.png',
        destacado: true,
        capturas: [],
        confidencial: true,
        historia: [
            'Una distribuidora industrial vendía a sus revendedores como se vendió siempre: por teléfono, por WhatsApp y por mail. El vendedor consultaba el stock en el ERP, tipeaba el pedido a mano y lo volvía a cargar en el sistema. Cada pedido pasaba dos veces por manos humanas, y cada paso era una chance de equivocarse.',
            'El pedido no era "hacer una tienda". Era una condición innegociable: el ERP sigue siendo el dueño de la verdad. Los artículos, los precios, el stock y los clientes viven ahí, y el portal no puede inventar nada ni quedar desfasado. Si un revendedor ve stock en la web, ese stock tiene que existir.',
            'Eso convirtió el proyecto en un problema de sincronización más que de e-commerce. Pasamos semanas leyendo la API del ERP antes de escribir una línea de portal. El catálogo se sincroniza completo, pero los precios y el stock van por su propia cadencia porque no cambian al mismo ritmo, y el circuito de venta tiene dos frecuencias: un pulso barato cada diez minutos y una corrida completa por hora. Nada de eso se nota desde afuera, que es exactamente el punto.',
            'Arriba de esa base pusimos lo que el negocio pedía: login por CUIT con la lista de precios que le corresponde a cada revendedor, carrito, checkout que genera la Nota de Pedido directo en el ERP, facturación electrónica contra AFIP, reglas de pricing configurables y un módulo de análisis comercial para que el dueño vea qué se vende y a quién.',
            'El portal comparte 290 commits de historia con nuestro SaaS de tienda, del que salió como fork. Esa es la ventaja de haber construido la base una vez bien: el segundo cliente no empieza de cero, empieza en el kilómetro 40.',
        ],
        desafio:
            'Poner una tienda online arriba de un ERP que no se puede tocar y que tiene que seguir siendo la única fuente de verdad de artículos, precios, stock y clientes — sin que el revendedor vea nunca un dato desfasado.',
        solucion:
            'Una capa de sincronización con cadencias separadas por tipo de dato, catálogo enriquecido localmente sin pisar el ERP, login por CUIT con lista de precios por revendedor, y checkout que genera la Nota de Pedido directo en el sistema del cliente.',
        metricas: [
            { valor: '1', label: 'sola carga por pedido (antes: dos)' },
            { valor: '10 min', label: 'de latencia máxima del stock' },
            { valor: '290', label: 'commits de base reutilizada del SaaS' },
        ],
        tecnologias: ['Angular', 'Java 21', 'Spring Boot 3', 'MySQL', 'Redis', 'Liquibase', 'Docker', 'AFIP WSFE', 'Mercado Pago', 'WebSocket / STOMP'],
        rol: 'Análisis funcional, arquitectura, desarrollo full-stack e infraestructura',
        duracion: 'En evolución continua',
    },

    // ─────────────────────────────────────────────────────────────────────────
    {
        slug: 'tienda-admin-saas',
        titulo: 'Tienda Admin — SaaS multicuenta de e-commerce',
        subtitulo: 'Un panel que unifica MercadoLibre y TiendaNube, factura en AFIP y cobra su propia suscripción',
        cliente: 'Producto propio de Iron Five',
        anio: '2026',
        categoria: 'SaaS',
        resumen:
            'Producto propio: publicaciones, ventas, stock y facturación de varias tiendas en un solo lugar, ' +
            'con reglas de pricing y análisis comercial.',
        imagen: '/images/casos/tienda-admin-portada.svg',
        imagenSocial: '/images/logo.png',
        destacado: true,
        capturas: [],
        confidencial: true,
        historia: [
            'Tienda Admin no salió de un brief. Salió de escuchar la misma queja tres veces: el que vende en MercadoLibre y en TiendaNube al mismo tiempo vive con cuatro pestañas abiertas, y el stock no le cierra nunca.',
            'La plataforma unifica eso. Conecta las dos cuentas por OAuth, baja publicaciones y ventas, mantiene el stock sincronizado y deja todo en un panel. Arriba de esa base pusimos lo que el vendedor pide en cuanto deja de apagar incendios: reglas de pricing que se aplican solas, análisis comercial y de clientes, métricas de MercadoLibre Ads, y facturación electrónica contra AFIP con un circuito que no obliga a nadie a abrir el sitio de la AFIP.',
            'Hay un detalle del que estamos particularmente contentos, porque resuelve algo real con casi nada de tecnología: antes de mandar una factura por mail, el sistema le pide autorización al admin **por Telegram**. Le llega el mensaje al celular, contesta "OK" y la factura sale. Sin app, sin login, sin panel. Cuando la manera más simple de resolver algo es también la más cómoda para el usuario, no hay que buscarle la vuelta.',
            'Es multicuenta de verdad: varias empresas conviven aisladas en la misma instalación, cada una con sus usuarios, sus roles y su suscripción, que se cobra sola por Mercado Pago. Y es el producto del que después salió el portal B2B como fork — construir la base bien una vez nos dio el segundo proyecto casi hecho.',
            'Hoy tiene una cuenta productiva andando. Lo mostramos porque es la prueba más directa de lo que hacemos: no solo tomamos requerimientos, también construimos producto propio y lo bancamos en producción.',
        ],
        desafio:
            'El vendedor que opera en varios canales a la vez pierde el control del stock y vive saltando entre paneles que no se hablan. Y la facturación queda siempre para el final, hecha a mano.',
        solucion:
            'Un SaaS multicuenta que conecta MercadoLibre y TiendaNube por OAuth, unifica publicaciones, ventas y stock, aplica reglas de pricing automáticas, factura contra AFIP con autorización por Telegram y cobra su propia suscripción por Mercado Pago.',
        metricas: [
            { valor: '2', label: 'canales de venta unificados' },
            { valor: '1', label: 'cuenta productiva en marcha' },
            { valor: 'Telegram', label: 'autorización de facturas desde el celular' },
        ],
        tecnologias: ['Angular 18', 'Java 21', 'Spring Boot 3', 'MySQL', 'Redis', 'Docker', 'AFIP WSFE', 'API MercadoLibre', 'API TiendaNube', 'Mercado Pago', 'Telegram Bot API'],
        rol: 'Producto propio: idea, diseño, desarrollo, infraestructura y operación',
        duracion: 'En evolución continua',
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Trabajos anteriores. Vienen de la lista original del sitio; conservan su id como
    // slug legible. Todavía no tienen ficha larga: la historia es corta a propósito.
    {
        slug: 'ecommerce-repuestos-automotores',
        titulo: 'E-Commerce de repuestos automotores',
        subtitulo: 'Plataforma comprador/vendedor de repuestos para autos y camiones',
        anio: '2025',
        categoria: 'E-Commerce',
        resumen: 'Plataforma integral comprador/vendedor para venta de repuestos de autos y camiones.',
        imagen: '/images/trabajo1.png',
        destacado: false,
        capturas: [{ src: '/images/trabajo1.png', alt: 'E-Commerce de repuestos automotores' }],
        historia: [
            'Un marketplace de repuestos donde conviven dos lados con necesidades opuestas: el que busca una pieza puntual para un modelo puntual, y el que tiene un depósito lleno y necesita publicarlo sin cargar mil fichas a mano.',
            'La plataforma es PWA: se instala en el celular y funciona con conexión intermitente, que es la realidad del que está en un taller o en un depósito.',
        ],
        metricas: [],
        tecnologias: ['React', 'Java', 'Spring Boot', 'Bootstrap 5', 'PWA'],
    },
    {
        slug: 'app-integracion-iot',
        titulo: 'App de integración IoT',
        subtitulo: 'Aplicación web y móvil integrada a dispositivos de Industria 4.0',
        anio: '2022',
        categoria: 'IoT',
        resumen: 'Aplicación móvil/web con integración a dispositivos enfocados a Industria 4.0.',
        imagen: '/images/trabajo2.png',
        destacado: false,
        capturas: [{ src: '/images/trabajo2.png', alt: 'App de integración IoT' }],
        historia: [
            'Integración con dispositivos industriales para monitoreo y control en tiempo real. El desafío no fue la interfaz: fue que los datos de planta lleguen completos y a tiempo, y que la app siga siendo usable cuando el dispositivo deja de responder.',
        ],
        metricas: [],
        tecnologias: ['React', 'Java', 'Spring Boot', 'Bootstrap 5', 'Redux', 'PWA'],
    },
    {
        slug: 'seguimiento-proyectos-municipales',
        titulo: 'Sistema de seguimiento de proyectos municipales',
        subtitulo: 'Portal público y backoffice de gestión de obra pública',
        anio: '2023',
        categoria: 'Sistemas de gestión',
        resumen: 'Aplicación pública y administración completa de proyectos municipales.',
        imagen: '/images/trabajo3.png',
        destacado: false,
        capturas: [{ src: '/images/trabajo3.png', alt: 'Sistema de seguimiento de proyectos municipales' }],
        historia: [
            'Dos sistemas en uno: un portal donde el vecino ve en qué anda cada proyecto de su municipio, y un backoffice donde las áreas cargan el avance con un workflow de aprobaciones.',
            'La parte difícil de la transparencia no es publicar los datos: es que el circuito interno sea lo bastante cómodo como para que los datos estén actualizados.',
        ],
        metricas: [],
        tecnologias: ['React', 'Java', 'Spring Boot', 'Bootstrap 5', 'Redux', 'PWA', 'MySQL'],
    },
    {
        slug: 'sistema-integral-rrhh',
        titulo: 'Sistema integral de RRHH',
        subtitulo: 'Agentes, licencias y evaluaciones de desempeño en un solo sistema',
        cliente: 'Instituto estatal',
        anio: '2018',
        categoria: 'Sistemas de gestión',
        resumen: 'Gestión de RRHH con administración de agentes, licencias y evaluaciones de desempeño.',
        imagen: '/images/trabajo4.png',
        destacado: false,
        capturas: [{ src: '/images/trabajo4.png', alt: 'Sistema integral de RRHH' }],
        historia: [
            'Administración de agentes, régimen de licencias y evaluaciones de desempeño para un instituto estatal. Reglas de licencias con muchas excepciones y un circuito de evaluación con varios evaluadores por agente.',
        ],
        metricas: [],
        tecnologias: ['Java', 'React', 'MSSQL', 'HTML'],
    },
    {
        slug: 'logistica-de-envios',
        titulo: 'Sistema de logística de envíos',
        subtitulo: 'Plataforma de administración para una empresa de logística',
        anio: '2023',
        categoria: 'Sistemas de gestión',
        resumen: 'Plataforma para la administración de una importante empresa de logística.',
        imagen: '/images/trabajo5.png',
        destacado: false,
        capturas: [{ src: '/images/trabajo5.png', alt: 'Sistema de logística de envíos' }],
        historia: [
            'Administración de envíos punta a punta para una empresa de logística: alta, seguimiento de estados y liquidación.',
        ],
        metricas: [],
        tecnologias: ['Java', 'Angular', 'PostgreSQL', 'HTML'],
    },
    {
        slug: 'gestion-licitaciones',
        titulo: 'Administración de pedidos de licitaciones',
        subtitulo: 'Solicitudes, proveedores y workflow de aprobación personalizado',
        cliente: 'Instituto estatal',
        anio: '2022',
        categoria: 'Sistemas de gestión',
        resumen:
            'Carga de solicitudes y proveedores, con un workflow personalizable para la gestión de las mismas.',
        imagen: '/images/trabajo6.png',
        destacado: false,
        capturas: [{ src: '/images/trabajo6.png', alt: 'Administración de pedidos de licitaciones' }],
        historia: [
            'Circuito de compras del Estado: solicitud, proveedores, cotizaciones y un workflow de aprobación que cada área configura según su propio régimen.',
            'El workflow configurable fue la decisión central del proyecto. Hardcodear el circuito hubiera sido más rápido de construir y hubiera envejecido en un año.',
        ],
        metricas: [],
        tecnologias: ['Java', 'React', 'PostgreSQL', 'HTML'],
    },
    {
        slug: 'gestion-proyectos-presupuestos',
        titulo: 'Gestión de proyectos y presupuestos',
        subtitulo: 'Presupuestos con niveles de aprobación y dashboards a medida',
        cliente: 'Instituto estatal',
        anio: '2020–2025',
        categoria: 'Sistemas de gestión',
        resumen:
            'Carga de proyectos con presupuesto, niveles de aprobación y dashboards con reportes y gráficos.',
        imagen: '/images/trabajo7.png',
        destacado: false,
        capturas: [{ src: '/images/trabajo7.png', alt: 'Gestión de proyectos y presupuestos' }],
        historia: [
            'Cinco años de acompañamiento sobre el mismo sistema: carga de proyectos con presupuesto, varios niveles de aprobación y dashboards con reportes y gráficos armados con el área que los usa.',
            'Los proyectos que duran cinco años no se ganan con la propuesta inicial. Se ganan estando cuando algo falla.',
        ],
        metricas: [{ valor: '5 años', label: 'de acompañamiento continuo' }],
        tecnologias: ['Java', 'React', 'MSSQL', 'HTML'],
    },
];

export const proyectosDestacados = proyectos.filter((p) => p.destacado);

export const getProyecto = (slug?: string): Proyecto | undefined =>
    proyectos.find((p) => p.slug === slug);

/** Categorías presentes, para los filtros del portafolio. */
export const categorias = ['Todos', ...Array.from(new Set(proyectos.map((p) => p.categoria)))];
